import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import mailIcon from "@/assets/Icons/Mail.png";
import targetIcon from "@/assets/Icons/Target.png";
import cogwheelIcon from "@/assets/Icons/Cogwheel.png";
import EmailIllustration from "@/components/EmailIllustration";
import heroBg from "@/assets/hero-bg-new.png";
import { AnimatedSection } from "@/components/AnimatedSection";

const EmailAssistant = () => {
  const { language } = useLanguage();

  const content = {
    is: {
      title: "PóstFulltrúi",
      subtitle: "Gervigreindardrifin tölvupóstsþjónusta",
      description: "PóstFulltrúi les, flokkar, setur í drög og svarar tölvupósti sjálfkrafa, sparar tíma og tryggir að enginn póstur verður ósvaraður.",
      features: [
        {
          icon: mailIcon,
          title: "Sjálfvirk flokkun og gæðaeftirlit",
          description: "Greinir og flokkar tölvupósta eftir forgangi og tryggir að öll svör séu fagleg og nákvæm.",
        },
        {
          icon: targetIcon,
          title: "Snjöll svör",
          description: "Býr til og sendir viðeigandi svör við algengum fyrirspurnum í samræmi við stefnu fyrirtækisins.",
        },
        {
          icon: cogwheelIcon,
          title: "Sérsniðin fyrir þitt fyrirtæki",
          description: "Virkar með öllum helstu tölvupóstþjónustum og aðlagast þínum vinnuferlum.",
        },
      ],
    },
    en: {
      title: "Email Assistant",
      subtitle: "AI-powered email service",
      description: "Email Assistant reads, categorizes, and responds to emails automatically, saving time and ensuring no email slips through.",
      features: [
        {
          icon: mailIcon,
          title: "Automatic Classification & Quality Control",
          description: "Identifies and categorizes emails by priority and ensures all responses are professional and accurate.",
        },
        {
          icon: targetIcon,
          title: "Smart Responses",
          description: "Creates and sends appropriate responses to common inquiries aligned with company policy.",
        },
        {
          icon: cogwheelIcon,
          title: "Customized for Your Business",
          description: "Works with all major email services and adapts to your AIs.",
        },
      ],
    },
  };

  const t = content[language];

  return (
    <main className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${heroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/65 via-background/60 to-background" />
        </div>

        <div className="container px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <img
              src={mailIcon}
              alt={t.title}
              className="h-32 w-32 mx-auto mb-6 object-contain"
              loading="lazy"
            />
            <h1 className="mb-6">{t.title}</h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8">
              {t.subtitle}
            </p>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.description}
            </p>
          </div>
        </div>
      </section>

      {/* Illustration Section */}
      <AnimatedSection>
        <section className="py-20">
          <div className="container px-6 md:px-8">
            <EmailIllustration />
          </div>
        </section>
      </AnimatedSection>

      {/* Features Section */}
      <AnimatedSection delay={0.1}>
        <section className="py-20">
          <div className="container px-6 md:px-8">
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {t.features.map((feature, index) => (
                <div
                  key={index}
                  className="p-8 rounded-lg border border-border bg-card hover:shadow-lg transition-all animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="h-24 w-24 mb-4 object-contain"
                    loading="lazy"
                  />
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <Footer />
    </main>
  );
};

export default EmailAssistant;
